import {announce} from '@github-ui/aria-live'
import {testIdProps} from '@github-ui/test-id-props'
import {BookIcon} from '@primer/octicons-react'
import {Blankslate} from '@primer/react/experimental'
import {useEffect} from 'react'

import {getInitialState} from '../helpers/initial-state'
import {ViewerPrivileges} from '../helpers/viewer-privileges'
import {IssueFieldResources} from '../strings'
import styles from './add-issue-fields-dialog.module.css'

type Props = {
  reason: 'all-org-only-filtered' | 'all-added' | 'no-fields-exist'
}

/**
 * Empty state shown in the Add issue fields dialog when no issue fields are available to add.
 *
 * Three variants are rendered depending on why no fields are available:
 * - When every organization field is set to "Organization only" (and therefore filtered out for a
 *   public/internal project), we explain the visibility restriction.
 * - When all available issue fields are already added to this project, we explain that state.
 * - Otherwise no issue fields exist yet, so we point admins to org settings to create some.
 */
export function AddIssueFieldsEmptyState({reason}: Props) {
  const {projectOwner} = getInitialState()
  const {hasAdminPermissions} = ViewerPrivileges()

  const isOrganization = projectOwner?.type === 'organization'
  const orgLogin = projectOwner?.login
  const showManageFieldsLink = hasAdminPermissions && isOrganization && !!orgLogin
  const manageFieldsHref = orgLogin ? `/organizations/${orgLogin}/settings/issue-fields` : undefined

  const description =
    reason === 'all-org-only-filtered'
      ? IssueFieldResources.EmptyState.allOrgOnlyDescription
      : reason === 'all-added'
        ? IssueFieldResources.EmptyState.allAddedDescription
        : IssueFieldResources.EmptyState.noFieldsDescription

  useEffect(() => {
    announce(`${IssueFieldResources.EmptyState.heading}. ${description}`)
  }, [description])

  return (
    <Blankslate size="small" className={styles.emptyState} {...testIdProps('add-issue-fields-empty-state')}>
      <Blankslate.Visual>
        <BookIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>{IssueFieldResources.EmptyState.heading}</Blankslate.Heading>
      <Blankslate.Description>{description}</Blankslate.Description>
      {showManageFieldsLink && (
        <Blankslate.PrimaryAction href={manageFieldsHref} {...testIdProps('add-issue-fields-manage-fields-link')}>
          {IssueFieldResources.EmptyState.manageFieldsLinkText}
        </Blankslate.PrimaryAction>
      )}
    </Blankslate>
  )
}
